import React, { useState, useMemo, useEffect } from 'react';
import './ProductTrackingTables.css';
import { TraceCode, IngredientTraceCode } from '../../../types/traceCode';
import { Recipe } from '../../../types';
import { BatchRecord } from '../../../types/batchRecord';
import { DeliveryLog } from '../../../types/deliveryLog';
import { createBlankTraceCode } from '../../../store/slices/traceCodesSlice';
import traceCodeApiService from '../../../services/traceCodeApiService';
import { groupItemsBySection, getDefaultExpandedState } from '../../../utils/haccpSectionUtils';
import { HaccpSectionHeader } from '../../../components/haccp/HaccpSectionHeader';
import { ClickableIdentifier } from '../../../components/haccp/ClickableIdentifier';
import { useTraceability } from '../../../components/haccp/TraceabilityContext';
import { matchesSearchQuery } from '../../../utils/searchUtils';
import { scrollToFieldAndHighlight } from '../../../utils/scrollUtils';
import { formatPanQuantityDisplay } from '../../../utils/panQuantity';

interface TraceCodeListProps {
  traceCodes: TraceCode[];
  fillings: Recipe[];
  batchRecords: BatchRecord[];
  deliveryLogs: DeliveryLog[];
  onAdd: (traceCode: TraceCode) => void;
  onUpdate: (traceCode: TraceCode) => void;
  onDelete: (id: string) => void;
  targetTraceCodeId?: string;
  targetField?: string;
  isFlagged?: boolean;
}

export const TraceCodeList: React.FC<TraceCodeListProps> = ({
  traceCodes,
  fillings,
  batchRecords,
  deliveryLogs,
  onAdd,
  onUpdate,
  onDelete,
  targetTraceCodeId,
  targetField,
  isFlagged
}) => {
  const { openTraceability } = useTraceability();
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTraceCode, setNewTraceCode] = useState<TraceCode>(createBlankTraceCode());
  const [editTraceCode, setEditTraceCode] = useState<TraceCode | null>(null);
  const [newIngredientTraceCodes, setNewIngredientTraceCodes] = useState<IngredientTraceCode[]>(
    []
  );
  const [editIngredientTraceCodes, setEditIngredientTraceCodes] = useState<
    IngredientTraceCode[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter trace codes based on search
  const filteredTraceCodes = useMemo(() => {
    if (!searchTerm.trim()) return traceCodes;
    return traceCodes.filter(code => matchesSearchQuery(code, searchTerm, [code.fillingName || '']));
  }, [traceCodes, searchTerm]);

  // Group trace codes into sections by productionDate
  const sections = useMemo(
    () => groupItemsBySection(filteredTraceCodes, (code) => code.productionDate),
    [filteredTraceCodes]
  );

  // Track expanded/collapsed state for each section
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    getDefaultExpandedState(sections)
  );

  // Auto-expand section containing target trace code for deep-linking
  useEffect(() => {
    if (!targetTraceCodeId) return;
    for (const section of sections) {
      if (section.items.some(code => code.id === targetTraceCodeId)) {
        setExpandedSections(prev => ({ ...prev, [section.key]: true }));
        break;
      }
    }
  }, [targetTraceCodeId, sections]);

  // Field-level deep-linking from admin review
  useEffect(() => {
    if (!targetTraceCodeId || !targetField) return;
    const timer = setTimeout(() => {
      scrollToFieldAndHighlight(`tracecode-${targetTraceCodeId}-field-${targetField}`, {
        highlightColor: isFlagged ? 'red' : 'blue'
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [targetTraceCodeId, targetField, isFlagged]);

  // Update expanded state when sections change
  useEffect(() => {
    setExpandedSections((prev) => {
      const newState = { ...prev };
      let hasChanges = false;
      for (const section of sections) {
        if (!(section.key in newState)) {
          newState[section.key] = false;
          hasChanges = true;
        }
      }
      return hasChanges ? newState : prev;
    });
  }, [sections]);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    padding: '20px',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s',
  };

  const addButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#4CAF50',
    color: 'white',
    marginBottom: '16px',
  };

  const saveButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#4CAF50',
    color: 'white',
    marginRight: '8px',
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#757575',
    color: 'white',
  };

  const editButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#2196F3',
    color: 'white',
    marginRight: '8px',
  };

  const deleteButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#f44336',
    color: 'white',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  const thStyle: React.CSSProperties = {
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    backgroundColor: '#f8f9fa',
    fontWeight: '600',
    fontSize: '13px',
    textTransform: 'uppercase',
    color: '#555',
  };

  const tdStyle: React.CSSProperties = {
    padding: '12px',
    borderBottom: '1px solid #eee',
    fontSize: '14px',
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    backgroundColor: 'white',
  };

  const readOnlyInputStyle: React.CSSProperties = {
    ...inputStyle,
    backgroundColor: '#f5f5f5',
    color: '#666',
  };

  const emptyStateStyle: React.CSSProperties = {
    padding: '40px',
    textAlign: 'center',
    color: '#888',
    backgroundColor: 'white',
    border: '1px solid #eee',
    borderRadius: '4px',
  };

  // Get batch records filtered by selected filling
  const getFilteredBatchRecords = (fillingId: string): BatchRecord[] => {
    if (!fillingId) return [];
    return batchRecords
      .filter((br) => br.productId === fillingId)
      .sort((a, b) => new Date(b.dateOfProduction).getTime() - new Date(a.dateOfProduction).getTime());
  };

  // Get delivery logs filtered by selected filling
  const getFilteredDeliveryLogs = (fillingId: string): DeliveryLog[] => {
    if (!fillingId) return [];
    return deliveryLogs.filter((dl) => dl.fillingId === fillingId);
  };

  // Validate required fields
  const validateTraceCode = (tc: TraceCode): boolean => {
    if (
      !tc.fillingId ||
      !tc.productionDate ||
      !tc.batchRecordId ||
      !tc.quantityProduced ||
      !tc.packagedBy
    ) {
      alert('Please fill in all required fields');
      return false;
    }

    if (tc.quantityProduced <= 0) {
      alert('Quantity produced must be greater than 0');
      return false;
    }

    return true;
  };

  // Render ingredient trace codes
  const renderIngredientTraceCodes = (ingredients: IngredientTraceCode[]) => {
    if (!ingredients || ingredients.length === 0) {
      return <span style={{ color: '#999', fontSize: '12px' }}>Select batch first</span>;
    }

    return (
      <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
        {ingredients.map((ing) => (
          <div key={ing.id}>
            <strong>{ing.ingredientName}:</strong>{' '}
            {ing.lotNumbers && ing.lotNumbers.length > 0
              ? ing.lotNumbers.map((lot, idx) => (
                  <React.Fragment key={lot + idx}>
                    {idx > 0 && ', '}
                    <ClickableIdentifier value={lot} type="lotNumber" onClick={openTraceability} />
                  </React.Fragment>
                ))
              : 'N/A'}
          </div>
        ))}
      </div>
    );
  };

  // Handlers for adding new trace code
  const handleStartAdd = () => {
    setNewTraceCode(createBlankTraceCode());
    setNewIngredientTraceCodes([]);
    setIsAddingNew(true);
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewTraceCode(createBlankTraceCode());
    setNewIngredientTraceCodes([]);
  };

  const handleSaveNew = () => {
    if (!validateTraceCode(newTraceCode)) return;

    // Find filling, batch, and delivery log to populate joined data
    const filling = fillings.find((f) => f.id === newTraceCode.fillingId);
    const batch = batchRecords.find((b) => b.id === newTraceCode.batchRecordId);
    const deliveryLog = deliveryLogs.find((dl) => dl.id === newTraceCode.deliveryLogId);

    const codeToSave = {
      ...newTraceCode,
      fillingName: filling?.name || '',
      productCode: (filling as any)?.productCode || '',
      batchLotNumber: batch?.batchLotNumber || '',
      batchProductionDate: batch?.dateOfProduction || '',
      deliveryDate: deliveryLog?.deliveryDate || null,
      customerDestination: deliveryLog?.customerDestination || '',
    };

    onAdd(codeToSave);
    handleCancelAdd();
  };

  const handleNewTraceCodeChange = async (field: keyof TraceCode, value: any) => {
    setNewTraceCode((prev) => {
      const updated = { ...prev, [field]: value };

      // If filling changes, reset dependent fields
      if (field === 'fillingId') {
        const filling = fillings.find((f) => f.id === value);
        updated.fillingName = filling?.name || '';
        updated.productCode = (filling as any)?.productCode || '';
        updated.batchRecordId = '';
        updated.batchLotNumber = '';
        updated.batchProductionDate = '';
        updated.deliveryLogId = null;
        updated.deliveryDate = null;
        updated.customerDestination = '';
        setNewIngredientTraceCodes([]);
      }

      // If batch changes, load ingredient trace codes
      if (field === 'batchRecordId' && value) {
        const batch = batchRecords.find((b) => b.id === value);
        updated.batchLotNumber = batch?.batchLotNumber || '';
        updated.batchProductionDate = batch?.dateOfProduction || '';

        // Fetch ingredient trace codes asynchronously
        traceCodeApiService
          .fetchIngredientTraceCodesByBatch(value)
          .then((ingredients) => {
            setNewIngredientTraceCodes(ingredients);
          })
          .catch((error) => {
            console.error('Failed to load ingredient trace codes:', error);
            setNewIngredientTraceCodes([]);
          });
      }

      // If delivery log changes, populate date shipped and customer
      if (field === 'deliveryLogId') {
        const log = deliveryLogs.find((dl) => dl.id === value);
        updated.deliveryDate = log?.deliveryDate || null;
        updated.customerDestination = log?.customerDestination || '';
      }

      return updated;
    });
  };

  // Handlers for editing existing trace code
  const handleStartEdit = (code: TraceCode) => {
    setEditingId(code.id);
    setEditTraceCode({ ...code });

    // Load ingredient trace codes for the batch
    if (code.batchRecordId) {
      traceCodeApiService
        .fetchIngredientTraceCodesByBatch(code.batchRecordId)
        .then((ingredients) => {
          setEditIngredientTraceCodes(ingredients);
        })
        .catch((error) => {
          console.error('Failed to load ingredient trace codes:', error);
          setEditIngredientTraceCodes([]);
        });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTraceCode(null);
    setEditIngredientTraceCodes([]);
  };

  const handleSaveEdit = () => {
    if (!editTraceCode || !validateTraceCode(editTraceCode)) return;

    // Find filling, batch, and delivery log to populate joined data
    const filling = fillings.find((f) => f.id === editTraceCode.fillingId);
    const batch = batchRecords.find((b) => b.id === editTraceCode.batchRecordId);
    const deliveryLog = deliveryLogs.find((dl) => dl.id === editTraceCode.deliveryLogId);

    const codeToSave = {
      ...editTraceCode,
      fillingName: filling?.name || '',
      productCode: (filling as any)?.productCode || '',
      batchLotNumber: batch?.batchLotNumber || '',
      batchProductionDate: batch?.dateOfProduction || '',
      deliveryDate: deliveryLog?.deliveryDate || null,
      customerDestination: deliveryLog?.customerDestination || '',
    };

    onUpdate(codeToSave);
    handleCancelEdit();
  };

  const handleEditTraceCodeChange = async (field: keyof TraceCode, value: any) => {
    if (!editTraceCode) return;

    setEditTraceCode((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, [field]: value };

      // If filling changes, reset dependent fields
      if (field === 'fillingId') {
        const filling = fillings.find((f) => f.id === value);
        updated.fillingName = filling?.name || '';
        updated.productCode = (filling as any)?.productCode || '';
        updated.batchRecordId = '';
        updated.batchLotNumber = '';
        updated.batchProductionDate = '';
        updated.deliveryLogId = null;
        updated.deliveryDate = null;
        updated.customerDestination = '';
        setEditIngredientTraceCodes([]);
      }

      // If batch changes, load ingredient trace codes
      if (field === 'batchRecordId' && value) {
        const batch = batchRecords.find((b) => b.id === value);
        updated.batchLotNumber = batch?.batchLotNumber || '';
        updated.batchProductionDate = batch?.dateOfProduction || '';

        // Fetch ingredient trace codes asynchronously
        traceCodeApiService
          .fetchIngredientTraceCodesByBatch(value)
          .then((ingredients) => {
            setEditIngredientTraceCodes(ingredients);
          })
          .catch((error) => {
            console.error('Failed to load ingredient trace codes:', error);
            setEditIngredientTraceCodes([]);
          });
      }

      // If delivery log changes, populate date shipped and customer
      if (field === 'deliveryLogId') {
        const log = deliveryLogs.find((dl) => dl.id === value);
        updated.deliveryDate = log?.deliveryDate || null;
        updated.customerDestination = log?.customerDestination || '';
      }

      return updated;
    });
  };

  // Handler for delete
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this trace code?')) {
      onDelete(id);
    }
  };

  // Render form row (for adding or editing)
  const renderFormRow = (
    code: TraceCode,
    ingredients: IngredientTraceCode[],
    onChange: (field: keyof TraceCode, value: any) => void,
    onSave: () => void,
    onCancel: () => void
  ) => {
    const filteredBatches = getFilteredBatchRecords(code.fillingId);
    const filteredDeliveryLogs = getFilteredDeliveryLogs(code.fillingId);

    return (
      <tr style={{ backgroundColor: '#f9f9f9' }}>
        <td style={tdStyle}>
          <input
            type="date"
            value={code.productionDate}
            onChange={(e) => onChange('productionDate', e.target.value)}
            style={inputStyle}
          />
        </td>
        <td style={tdStyle}>
          <select
            value={code.fillingId}
            onChange={(e) => onChange('fillingId', e.target.value)}
            style={selectStyle}
          >
            <option value="">Select product...</option>
            {fillings.map((filling) => (
              <option key={filling.id} value={filling.id}>
                {filling.name}
              </option>
            ))}
          </select>
        </td>
        <td style={tdStyle}>
          <input
            type="text"
            value={code.productCode}
            readOnly
            style={readOnlyInputStyle}
            placeholder="Auto-populated"
          />
        </td>
        <td style={tdStyle}>
          <select
            value={code.batchRecordId}
            onChange={(e) => onChange('batchRecordId', e.target.value)}
            style={selectStyle}
            disabled={!code.fillingId}
          >
            <option value="">
              {!code.fillingId
                ? 'Select product first'
                : filteredBatches.length === 0
                ? 'No batches for this product'
                : 'Select batch...'}
            </option>
            {filteredBatches.map((batch) => (
              <option key={batch.id} value={batch.id}>
                {batch.batchLotNumber} - {batch.dateOfProduction}
              </option>
            ))}
          </select>
        </td>
        <td style={{ ...tdStyle, maxWidth: '200px' }}>
          <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
            {renderIngredientTraceCodes(ingredients)}
          </div>
        </td>
        <td style={tdStyle}>
          {code.hasPans ? (
            <div>
              <span>{formatPanQuantityDisplay(code.panQuantityTotalPieces)}</span>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>(auto from pans)</div>
            </div>
          ) : (
            <>
              <input
                type="number"
                value={code.quantityProduced ?? ''}
                onChange={(e) => onChange('quantityProduced', parseInt(e.target.value) || 0)}
                style={inputStyle}
                min="0"
              />
              <span style={{ fontSize: '12px', color: '#666', marginLeft: '4px' }}>dozens</span>
            </>
          )}
        </td>
        <td style={tdStyle}>
          <input
            type="text"
            value={code.packagedBy}
            onChange={(e) => onChange('packagedBy', e.target.value)}
            style={inputStyle}
          />
        </td>
        <td style={tdStyle}>
          <input
            type="text"
            value={code.deliveryDate || ''}
            readOnly
            style={readOnlyInputStyle}
            placeholder="From delivery log"
          />
        </td>
        <td style={tdStyle}>
          <select
            value={code.deliveryLogId || ''}
            onChange={(e) => onChange('deliveryLogId', e.target.value || null)}
            style={selectStyle}
            disabled={!code.fillingId}
          >
            <option value="">None (not shipped yet)</option>
            {filteredDeliveryLogs.map((log) => (
              <option key={log.id} value={log.id}>
                {log.customerDestination}
              </option>
            ))}
          </select>
        </td>
        <td style={tdStyle}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={onSave} style={saveButtonStyle}>
              Save
            </button>
            <button onClick={onCancel} style={cancelButtonStyle}>
              Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  };

  // TraceCodeDisplayRow component - separate component to properly use hooks
  interface TraceCodeDisplayRowProps {
    code: TraceCode;
    onEdit: (code: TraceCode) => void;
    onDelete: (id: string) => void;
    tdStyle: React.CSSProperties;
    editButtonStyle: React.CSSProperties;
    deleteButtonStyle: React.CSSProperties;
    renderIngredientTraceCodes: (ingredients: IngredientTraceCode[]) => JSX.Element;
  }

  const TraceCodeDisplayRow: React.FC<TraceCodeDisplayRowProps> = ({
    code,
    onEdit,
    onDelete,
    tdStyle,
    editButtonStyle,
    deleteButtonStyle,
    renderIngredientTraceCodes,
  }) => {
    const [displayIngredients, setDisplayIngredients] = useState<IngredientTraceCode[]>([]);

    React.useEffect(() => {
      if (code.batchRecordId) {
        traceCodeApiService
          .fetchIngredientTraceCodesByBatch(code.batchRecordId)
          .then(setDisplayIngredients)
          .catch(() => setDisplayIngredients([]));
      }
    }, [code.batchRecordId]);

    return (
      <tr key={code.id} style={{ transition: 'background-color 0.2s' }}>
        <td id={`tracecode-${code.id}-field-productionDate`} style={tdStyle}>{code.productionDate}</td>
        <td id={`tracecode-${code.id}-field-fillingName`} style={tdStyle}>{code.fillingName || 'N/A'}</td>
        <td id={`tracecode-${code.id}-field-productCode`} style={tdStyle}>{code.productCode || 'N/A'}</td>
        <td id={`tracecode-${code.id}-field-batchLotNumber`} style={tdStyle}>
          {code.batchLotNumber ? (
            <ClickableIdentifier value={code.batchLotNumber} type="batchLotNumber" onClick={openTraceability} />
          ) : 'N/A'}
        </td>
        <td style={{ ...tdStyle, maxWidth: '200px' }}>
          <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
            {renderIngredientTraceCodes(displayIngredients)}
          </div>
        </td>
        <td id={`tracecode-${code.id}-field-quantityProduced`} style={tdStyle}>
          {code.hasPans
            ? formatPanQuantityDisplay(code.panQuantityTotalPieces)
            : `${code.quantityProduced ?? 0} dozens`}
        </td>
        <td id={`tracecode-${code.id}-field-packagedBy`} style={tdStyle}>{code.packagedBy}</td>
        <td id={`tracecode-${code.id}-field-deliveryDate`} style={tdStyle}>{code.deliveryDate || '-'}</td>
        <td id={`tracecode-${code.id}-field-customerDestination`} style={tdStyle}>{code.customerDestination || '-'}</td>
        <td style={tdStyle}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => onEdit(code)} style={editButtonStyle}>
              Edit
            </button>
            <button onClick={() => onDelete(code.id)} style={deleteButtonStyle}>
              Delete
            </button>
          </div>
        </td>
      </tr>
    );
  };

  // Table header component
  const renderTableHeader = () => (
    <thead>
      <tr>
        <th style={thStyle}>Production Date</th>
        <th style={thStyle}>Product Name</th>
        <th style={thStyle}>Product Code</th>
        <th style={thStyle}>Batch #</th>
        <th style={thStyle}>Ingredient Trace Codes</th>
        <th style={thStyle}>Quantity Produced</th>
        <th style={thStyle}>Packaged By</th>
        <th style={thStyle}>Date Shipped</th>
        <th style={thStyle}>Customer</th>
        <th style={thStyle}>Actions</th>
      </tr>
    </thead>
  );

  // Render a trace code row (handles both edit and display mode)
  const renderTraceCodeRow = (code: TraceCode) => {
    if (editingId === code.id && editTraceCode) {
      return renderFormRow(
        editTraceCode,
        editIngredientTraceCodes,
        handleEditTraceCodeChange,
        handleSaveEdit,
        handleCancelEdit
      );
    }
    return (
      <TraceCodeDisplayRow
        key={code.id}
        code={code}
        onEdit={handleStartEdit}
        onDelete={handleDelete}
        tdStyle={tdStyle}
        editButtonStyle={editButtonStyle}
        deleteButtonStyle={deleteButtonStyle}
        renderIngredientTraceCodes={renderIngredientTraceCodes}
      />
    );
  };

  return (
    <div style={containerStyle} className="table-container-mobile">
      {/* Add button */}
      <button
        onClick={handleStartAdd}
        style={addButtonStyle}
        disabled={isAddingNew || editingId !== null}
      >
        + Add Trace Code
      </button>

      {/* Add new form (shown as standalone table when adding) */}
      {isAddingNew && (
        <div className="table-wrapper-mobile">
          <table style={tableStyle}>
            {renderTableHeader()}
            <tbody>
              {renderFormRow(
                newTraceCode,
                newIngredientTraceCodes,
                handleNewTraceCodeChange,
                handleSaveNew,
                handleCancelAdd
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Search */}
      {!isAddingNew && !editingId && (
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Search trace codes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
              boxSizing: 'border-box' as const
            }}
          />
        </div>
      )}

      {/* Empty state */}
      {traceCodes.length === 0 && !isAddingNew && (
        <div style={emptyStateStyle}>
          No trace codes yet. Click &quot;Add Trace Code&quot; to create one.
        </div>
      )}

      {/* No search results */}
      {traceCodes.length > 0 && filteredTraceCodes.length === 0 && !isAddingNew && (
        <div style={emptyStateStyle}>
          No trace codes match your search for "{searchTerm}".
        </div>
      )}

      {/* Sections with trace codes */}
      {sections.map((section) => (
        <div key={section.key} style={{ marginBottom: '16px' }}>
          <HaccpSectionHeader
            label={section.label}
            itemCount={section.items.length}
            isExpanded={expandedSections[section.key] ?? !section.isTrialSection}
            isTrialSection={section.isTrialSection}
            onToggle={() => toggleSection(section.key)}
          />

          {(expandedSections[section.key] ?? !section.isTrialSection) && (
            <div className="table-wrapper-mobile">
              <table style={tableStyle}>
                {renderTableHeader()}
                <tbody>
                  {section.items.map((code) => renderTraceCodeRow(code))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
